import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Archive, AlertTriangle, RefreshCw, FileSearch, CheckCircle2, RotateCcw } from 'lucide-react';
import { AuthContext, API, formatDateWithEnglishNumerals } from '../shared';
import LifecycleBadge from './LifecycleBadge';

const SmartArchiveManagement = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [minAgeDays, setMinAgeDays] = useState(365);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, count: 0 });
  const [activeTab, setActiveTab] = useState('candidates');

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/lifecycle/stats`, authHeaders());
      setStats(r.data);
    } catch (e) {
      console.error('lifecycle/stats failed', e);
    }
  }, []);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/lifecycle/archive-candidates?min_age_days=${minAgeDays}`,
        authHeaders(),
      );
      setCandidates(r.data || []);
      setSelected(new Set());
    } catch (e) {
      console.error('archive-candidates failed', e);
    } finally {
      setLoading(false);
    }
  }, [minAgeDays]);

  const fetchArchived = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/lifecycle/archived`, authHeaders());
      setArchived(r.data || []);
    } catch (e) {
      console.error('archived list failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchCandidates();
  }, [fetchStats, fetchCandidates]);

  useEffect(() => {
    if (activeTab === 'archived') fetchArchived();
  }, [activeTab, fetchArchived]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map((c) => c.id)));
  };

  const doArchive = async () => {
    try {
      const ids = Array.from(selected);
      const r = await axios.post(
        `${API}/lifecycle/archive`,
        { operation_ids: ids, reason: 'أرشفة جماعية يدوية' },
        authHeaders(),
      );
      alert(`✅ تمت أرشفة ${r.data.archived_count} عملية بنجاح`);
      setConfirmDialog({ open: false, count: 0 });
      await fetchStats();
      await fetchCandidates();
    } catch (e) {
      alert(`❌ فشل: ${e.response?.data?.detail || e.message}`);
    }
  };

  const doReopen = async (operationId) => {
    if (user?.role !== 'super_admin') {
      alert('⚠️ فقط المدير العام يمكنه إعادة فتح الملف المؤرشف');
      return;
    }
    try {
      await axios.post(`${API}/lifecycle/reopen/${operationId}`, {}, authHeaders());
      alert('✅ تمّت إعادة فتح الملف بنجاح');
      await fetchStats();
      await fetchArchived();
    } catch (e) {
      alert(`❌ فشل: ${e.response?.data?.detail || e.message}`);
    }
  };

  // Permission gate: only managers + general accountants
  if (user && user.role === 'agency_staff') {
    return (
      <div className="p-6" dir="rtl">
        <Card>
          <CardContent className="p-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-800">⛔ هذه الصفحة للمدير العام والمحاسب فقط</h2>
            <p className="text-gray-500 mt-2">يرجى التواصل مع المحاسب لطلب أيّ أرشفة.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" dir="rtl" data-testid="smart-archive-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Archive className="h-7 w-7 text-brand-600" />
            🗄️ مركز الأرشفة الذكي
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            نظام Lifecycle Management — يفحص الملفات تلقائياً ويُرشّح القابل للأرشفة فقط (مدفوع بالكامل + بدون مشاكل + قديم)
          </p>
        </div>
        <Button
          onClick={() => { fetchStats(); fetchCandidates(); }}
          variant="outline"
          data-testid="refresh-stats-btn"
        >
          <RefreshCw className="h-4 w-4 ml-2" />
          تحديث
        </Button>
      </div>

      {/* Stats Cards — 5 colored Lifecycle Status counters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard color="green" icon="🟢" label="نشطة" value={stats?.active ?? '...'} />
        <StatCard color="yellow" icon="🟡" label="بانتظار الدفع" value={stats?.awaiting_payment ?? '...'} />
        <StatCard color="blue" icon="🔵" label="مكتملة" value={stats?.completed ?? '...'} />
        <StatCard color="rose" icon="🔴" label="مشكلة/دين" value={stats?.problem ?? '...'} />
        <StatCard color="gray" icon="⚫" label="مؤرشفة" value={stats?.archived ?? '...'} />
      </div>

      {/* Tabs: Candidates vs Archived */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="candidates" data-testid="tab-candidates">
            <FileSearch className="h-4 w-4 ml-2" />
            🟢 جاهز للأرشفة ({stats?.archive_ready_count ?? '...'})
          </TabsTrigger>
          <TabsTrigger value="archived" data-testid="tab-archived">
            <Archive className="h-4 w-4 ml-2" />
            ⚫ مؤرشف ({stats?.archived ?? '...'})
          </TabsTrigger>
        </TabsList>

        {/* Candidates Tab */}
        <TabsContent value="candidates">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                ملفات مطابقة لشروط الأرشفة
              </CardTitle>
              <CardDescription>
                الشروط: مدفوعة بالكامل • لا توجد ديون • لا استرجاع معلّق • غير مرتبطة بأقساط نشطة
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Min-age controls */}
              <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-md">
                <Label className="font-semibold">عمر الملف الأدنى (بالأيام):</Label>
                <Input
                  type="number"
                  min="30"
                  max="3650"
                  value={minAgeDays}
                  onChange={(e) => setMinAgeDays(Number(e.target.value) || 365)}
                  className="w-24 text-center"
                  data-testid="min-age-input"
                />
                <span className="text-xs text-gray-500">
                  (~{Math.round(minAgeDays / 30)} شهر)
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchCandidates}
                  data-testid="apply-age-btn"
                >
                  تطبيق
                </Button>
                <div className="flex gap-1 mr-auto">
                  {[180, 365, 730].map((d) => (
                    <Button
                      key={d}
                      size="sm"
                      variant={minAgeDays === d ? 'default' : 'outline'}
                      onClick={() => { setMinAgeDays(d); }}
                      className="text-xs"
                    >
                      {d === 180 ? '6 أشهر' : d === 365 ? 'سنة' : 'سنتان'}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Bulk action */}
              {candidates.length > 0 && (
                <div className="flex items-center justify-between bg-brand-50 p-3 rounded-md border border-brand-200">
                  <div className="text-sm text-gray-700">
                    {selected.size > 0
                      ? <strong className="text-brand-900">تمّ تحديد {selected.size} عملية</strong>
                      : <span>اختر العمليات لأرشفتها</span>}
                  </div>
                  <Button
                    onClick={() => setConfirmDialog({ open: true, count: selected.size })}
                    disabled={selected.size === 0}
                    className="bg-gray-700 hover:bg-gray-800 text-white"
                    data-testid="bulk-archive-btn"
                  >
                    <Archive className="h-4 w-4 ml-2" />
                    أرشفة المحدّد ({selected.size})
                  </Button>
                </div>
              )}

              {/* Candidates table */}
              <Table data-table-id="archive-candidates">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-center">
                      <input
                        type="checkbox"
                        checked={candidates.length > 0 && selected.size === candidates.length}
                        onChange={toggleAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead className="text-right">رقم العملية</TableHead>
                    <TableHead className="text-right">العميل</TableHead>
                    <TableHead className="text-right">الخدمة</TableHead>
                    <TableHead className="text-right">المبلغ</TableHead>
                    <TableHead className="text-right">العمر</TableHead>
                    <TableHead className="text-right">الوكالة</TableHead>
                    <TableHead className="text-right">الحالة</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">⏳ جاري التحميل...</TableCell></TableRow>
                  ) : candidates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                        <Archive className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                        لا توجد ملفات جاهزة للأرشفة وفق الشروط الحالية
                        <div className="text-xs text-gray-400 mt-2">
                          جرّب تخفيض "عمر الملف الأدنى" لرؤية المزيد
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : candidates.map((c) => (
                    <TableRow
                      key={c.id}
                      className={selected.has(c.id) ? 'bg-brand-50' : ''}
                      data-testid={`candidate-row-${c.id}`}
                    >
                      <TableCell className="text-center">
                        <input
                          type="checkbox"
                          checked={selected.has(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          data-testid={`select-candidate-${c.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium text-brand-700">{c.operation_no}</TableCell>
                      <TableCell>{c.client_name || 'غير محدد'}</TableCell>
                      <TableCell className="text-sm">{c.service_name || '-'}</TableCell>
                      <TableCell className="font-semibold">{c.final_price?.toLocaleString()} دج</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                          {c.age_days} يوم
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">{c.agency_name || '-'}</TableCell>
                      <TableCell><LifecycleBadge status={c.computed_status} size="xs" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Archived Tab */}
        <TabsContent value="archived">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Archive className="h-5 w-5 text-gray-600" />
                الأرشيف النهائي
              </CardTitle>
              <CardDescription>
                ملفات مؤرشفة (للقراءة فقط). يحتاج إلى المدير العام لإعادة فتح أيّ ملف.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table data-table-id="archived-list">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">رقم العملية</TableHead>
                    <TableHead className="text-right">العميل</TableHead>
                    <TableHead className="text-right">الخدمة</TableHead>
                    <TableHead className="text-right">المبلغ</TableHead>
                    <TableHead className="text-right">تاريخ الأرشفة</TableHead>
                    <TableHead className="text-right">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8">⏳ جاري التحميل...</TableCell></TableRow>
                  ) : archived.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-500">⚫ لا توجد ملفات مؤرشفة بعد</TableCell></TableRow>
                  ) : archived.map((op) => (
                    <TableRow key={op.id} data-testid={`archived-row-${op.id}`}>
                      <TableCell className="font-medium text-gray-700">{op.operation_no}</TableCell>
                      <TableCell>{op.client_name || 'غير محدد'}</TableCell>
                      <TableCell className="text-sm">{op.service_name || '-'}</TableCell>
                      <TableCell>{op.final_price?.toLocaleString()} دج</TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {op.archived_at ? formatDateWithEnglishNumerals(op.archived_at) : '-'}
                      </TableCell>
                      <TableCell>
                        {user?.role === 'super_admin' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => doReopen(op.id)}
                            className="text-orange-600 hover:text-orange-700"
                            data-testid={`reopen-${op.id}`}
                          >
                            <RotateCcw className="h-4 w-4 ml-1" />
                            إعادة فتح
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">للمدير العام فقط</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirm Archive Dialog */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(o) => { if (!o) setConfirmDialog({ open: false, count: 0 }); }}
      >
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle>📦 تأكيد الأرشفة</DialogTitle>
            <DialogDescription className="text-base text-gray-700 pt-2">
              هل أنت متأكد من أرشفة <strong className="text-brand-600">{confirmDialog.count}</strong> عملية؟
              <br /><br />
              ⚠️ بعد الأرشفة:
              <ul className="list-disc mr-6 mt-2 text-sm">
                <li>سيتم إخفاء الملفات من قائمة العمليات اليومية</li>
                <li>تبقى متاحة للقراءة في تبويب "مؤرشف"</li>
                <li>يحتاج المدير العام لإعادة فتح أيّ ملف</li>
                <li>يتم تسجيل العملية في سجلّ التدقيق</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row-reverse gap-2">
            <Button
              onClick={doArchive}
              className="bg-gray-700 hover:bg-gray-800 text-white"
              data-testid="confirm-archive-btn"
            >
              نعم، أرشف
            </Button>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, count: 0 })}
            >
              إلغاء
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ color, icon, label, value }) => {
  const colors = {
    green: 'border-green-200 bg-green-50',
    yellow: 'border-yellow-200 bg-yellow-50',
    blue: 'border-brand-200 bg-brand-50',
    rose: 'border-rose-200 bg-rose-50',
    gray: 'border-gray-300 bg-gray-100',
  };
  return (
    <Card className={`${colors[color]} border-2`}>
      <CardContent className="p-4 text-center">
        <div className="text-3xl mb-1">{icon}</div>
        <div className="text-xs text-gray-600 mb-1">{label}</div>
        <div className="text-2xl font-bold text-gray-800">{value}</div>
      </CardContent>
    </Card>
  );
};

export default SmartArchiveManagement;
